import React from 'react';
import { Briefcase, Users, FileText, Building2 } from 'lucide-react';

/**
 * Componente de 4 tablas compactas para el dashboard de Timesheets
 * ACTUALIZADO: Muestra TODOS los registros (sin límite de maxFilas)
 * MEJORADO: Filas clicables como filtros interactivos
 */
export default function TablasCompactas({
  horasProyectoData = [],
  horasTecnicoData = [],
  tipoRegistroData = [],
  resumenEquipoData = [],
  isDarkMode = true,
  onProyectoClick = null,
  onTecnicoClick = null,
  onTipoRegistroClick = null,
  onEquipoClick = null,
  selectedProyectos = [],
  selectedTecnicos = [],
  selectedTiposRegistro = [],
  selectedEquipos = []
}) {
  // Estilos
  const cardClasses = isDarkMode
    ? 'bg-[#071a0f]/80 backdrop-blur-md border border-[#00FF66]/20 rounded-xl shadow-lg hover:border-[#00FF66]/40 hover:shadow-[0_0_24px_rgba(0,255,102,0.14)] transition-all duration-300'
    : 'bg-white border border-emerald-200 rounded-xl shadow-lg hover:border-emerald-400 transition-all duration-300';

  const textPrimary = isDarkMode ? 'text-[#e8fff0]' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-[#a0c8b0]' : 'text-gray-600';
  const textMuted = isDarkMode ? 'text-[#7ea58f]' : 'text-gray-500';

  const tableHeaderBg = isDarkMode ? 'bg-[#00FF66]/8' : 'bg-emerald-50';
  const tableRowHover = isDarkMode ? 'hover:bg-[#00FF66]/5' : 'hover:bg-emerald-50';
  const tableBorder = isDarkMode ? 'border-[#00FF66]/15' : 'border-gray-200';
  const getRowSelectBg = (isSelected) => {
    if (isSelected) {
      return isDarkMode ? 'bg-[#00FF66]/15' : 'bg-emerald-100';
    }
    return '';
  };
  const getIconChipClasses = (color) => {
    if (isDarkMode) {
      return `p-2 rounded-lg border flex items-center justify-center`;
    }
    return 'p-2 rounded-lg border flex items-center justify-center';
  };

  // Función para formatear horas
  const formatHoras = (horas) => {
    if (!horas && horas !== 0) return '0';
    return parseFloat(horas).toLocaleString('es-MX', { 
      minimumFractionDigits: 0, 
      maximumFractionDigits: 2 
    });
  };

  // Componente de tabla genérica - SIN LÍMITE DE FILAS
  const TablaCompacta = ({ titulo, Icon, iconColor, columnas, datos, onRowClick = null, selectedValues = [] }) => {
    const iconContainerStyle = isDarkMode
      ? {
          backgroundColor: `${iconColor}26`,
          borderColor: `${iconColor}66`,
          boxShadow: `0 0 12px ${iconColor}33`
        }
      : {
          backgroundColor: `${iconColor}1A`,
          borderColor: `${iconColor}66`
        };

    return (
      <div className={`${cardClasses} p-4 flex flex-col h-full`}>
        {/* Header */}
        <div className={`flex items-center gap-2 mb-3 pb-2 border-b ${tableBorder}`}>
          <div className={getIconChipClasses(iconColor)} style={iconContainerStyle}>
            <Icon className="w-4 h-4" style={{ color: iconColor }} />
          </div>
          <div className="flex-1">
            <h3 className={`${textPrimary} text-sm font-bold`}>{titulo}</h3>
            <p className={`${textMuted} text-xs`}>{datos.length} registros</p>
          </div>
        </div>

        {/* Tabla con scroll - TODOS LOS DATOS */}
        <div
          className="flex-1 overflow-auto [scrollbar-gutter:stable] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar]:h-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full"
          style={{
            maxHeight: '400px',
            scrollbarWidth: 'thin',
            scrollbarColor: isDarkMode ? '#00FF66 #062115' : '#10B981 #ECFDF5'
          }}
        >
          <table className="w-full text-xs">
            <thead className={`${tableHeaderBg} sticky top-0 z-10`}>
              <tr>
                {columnas.map((col, idx) => (
                  <th
                    key={idx}
                    className={`px-2 py-2 text-left ${textSecondary} font-semibold border-b ${tableBorder} ${
                      col.align === 'right' ? 'text-right' : ''
                    }`}
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {datos.length === 0 ? (
                <tr>
                  <td colSpan={columnas.length} className={`px-2 py-4 text-center ${textMuted}`}>
                    No hay datos disponibles
                  </td>
                </tr>
              ) : (
                datos.map((fila, idx) => {
                  // Determinar si esta fila está seleccionada (comparar con el primer campo de la fila)
                  const primeraClave = columnas[0]?.key;
                  const valorFila = fila[primeraClave];
                  const isSelected = selectedValues.includes(valorFila);
                  
                  return (
                    <tr 
                      key={idx}
                      onClick={() => onRowClick && onRowClick(valorFila)}
                      className={`${tableRowHover} ${getRowSelectBg(isSelected)} transition-colors border-b ${tableBorder} ${
                        onRowClick ? 'cursor-pointer' : ''
                      }`}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if ((e.key === 'Enter' || e.key === ' ') && onRowClick) {
                          onRowClick(valorFila);
                        }
                      }}
                    >
                      {columnas.map((col, colIdx) => (
                        <td
                          key={colIdx}
                          className={`px-2 py-2 ${textPrimary} ${
                            col.align === 'right' ? 'text-right' : ''
                          } ${col.bold ? 'font-semibold' : ''}`}
                        >
                          {col.render ? col.render(fila) : fila[col.key]}
                        </td>
                      ))}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-4">
      {/* Tabla 1: Horas por Proyecto (Cliente) */}
      <TablaCompacta
        titulo="Horas por Cliente"
        Icon={Briefcase}
        iconColor="#00D4AA"
        columnas={[
          { key: 'proyecto', label: 'Cliente', align: 'left' },
          { 
            key: 'horas', 
            label: 'Horas', 
            align: 'right', 
            bold: true,
            render: (fila) => formatHoras(fila.horas)
          }
        ]}
        datos={horasProyectoData}
        onRowClick={onProyectoClick}
        selectedValues={selectedProyectos}
      />

      {/* Tabla 2: Horas por Técnico */}
      <TablaCompacta
        titulo="Horas por Técnico"
        Icon={Users}
        iconColor="#00B8FF"
        columnas={[
          { key: 'tecnico', label: 'Técnico', align: 'left' },
          { 
            key: 'horas', 
            label: 'Horas', 
            align: 'right', 
            bold: true,
            render: (fila) => formatHoras(fila.horas)
          }
        ]}
        datos={horasTecnicoData}
        onRowClick={onTecnicoClick}
        selectedValues={selectedTecnicos}
      />

      {/* Tabla 3: Horas por Tipo de Registro */}
      <TablaCompacta
        titulo="Horas por Registro"
        Icon={FileText}
        iconColor="#A56EFF"
        columnas={[
          { key: 'tipo', label: 'Tipo', align: 'left' },
          { 
            key: 'horas', 
            label: 'Horas', 
            align: 'right', 
            bold: true,
            render: (fila) => formatHoras(fila.horas)
          }
        ]}
        datos={tipoRegistroData}
        onRowClick={onTipoRegistroClick}
        selectedValues={selectedTiposRegistro}
      />

      {/* Tabla 4: Horas por Equipo */}
      <TablaCompacta
        titulo="Horas por Equipo"
        Icon={Building2}
        iconColor="#1EEB8A"
        columnas={[
          { key: 'equipo', label: 'Equipo', align: 'left' },
          { 
            key: 'horas', 
            label: 'Horas', 
            align: 'right', 
            bold: true,
            render: (fila) => formatHoras(fila.horas)
          }
        ]}
        datos={resumenEquipoData}
        onRowClick={onEquipoClick}
        selectedValues={selectedEquipos}
      />
    </div>
  );
}